import { IForm } from 'core-components/classes/models/IForm'
import { Direction, IDataProviderSort } from 'core-components/types'
import { TMultipleSort } from 'core-components/types/models/TMultipleSort'
import { TSingleSort } from 'core-components/types/models/TSingleSort'

export const getSortDirection = (
  sort: IDataProviderSort | IDataProviderSort[] | undefined,
  field: string
): Direction | null => {
  if (!sort) {
    return null
  }
  if (Array.isArray(sort)) {
    const sortedValue = sort.find((el) => el.field === field)
    if (sortedValue) {
      return sortedValue.dir
    }
    return null
  }
  if (sort.field === field) {
    return sort.dir
  }
  return null
}

const getSortValue = (field: string, direction: Direction): IDataProviderSort => {
  return {
    field,
    dir: direction,
  }
}

export const updateSortDirection = (
  currentSort: IDataProviderSort | IDataProviderSort[] | undefined,
  newDirection: Direction | null,
  field: string
): IDataProviderSort | IDataProviderSort[] | undefined => {
  if (!currentSort) {
    return getSortValue(field, 'asc')
  }

  if (Array.isArray(currentSort)) {
    if (newDirection === 'asc') {
      return [...currentSort, getSortValue(field, 'asc')]
    }
    if (newDirection === 'desc') {
      return currentSort.map((sortValue) => {
        if (sortValue.field === field) {
          return getSortValue(field, 'desc')
        }
        return sortValue
      })
    }
    return currentSort.filter((el) => el.field !== field)
  }

  if (newDirection === 'asc') {
    return [currentSort, getSortValue(field, 'asc')]
  }

  if (newDirection === 'desc') {
    return getSortValue(field, 'desc')
  }
  return undefined
}

const getNewDirection = (currentDirection: Direction | null): Direction | null => {
  if (currentDirection === 'asc') {
    return 'desc'
  }
  if (currentDirection === 'desc') {
    return null
  }
  return 'asc'
}

export const switchSortDirection = (
  currentSort: IDataProviderSort | IDataProviderSort[] | undefined,
  field: string
): IDataProviderSort | IDataProviderSort[] | undefined => {
  const currentDirection = getSortDirection(currentSort, field)
  const newDirection = getNewDirection(currentDirection)
  return updateSortDirection(currentSort, newDirection, field)
}

export const singleSort: TSingleSort = ({ firstItem, secondItem, sortData }) => {
  const firstItemData = firstItem[sortData.field] || ''
  const secondItemData = secondItem[sortData.field] || ''
  const isNumber = !Number.isNaN(+firstItemData) && !Number.isNaN(+secondItemData)
  if (isNumber) {
    return sortData.dir === 'asc' ? firstItemData - secondItemData : secondItemData - firstItemData
  }
  if (firstItemData === secondItemData) {
    return 0
  }
  if (
    (firstItemData > secondItemData && sortData.dir === 'asc') ||
    (secondItemData > firstItemData && sortData.dir === 'desc')
  ) {
    return 1
  }
  return -1
}

export const multipleSort: TMultipleSort = ({ firstItem, secondItem, sortData }) => {
  if (!sortData.length) {
    return 0
  }
  const currentSort = sortData[0]
  const sortResult = singleSort({ firstItem, secondItem, sortData: currentSort })
  if (sortResult === 0 && [...sortData].slice(1).length) {
    return multipleSort({ firstItem, secondItem, sortData: [...sortData].slice(1) })
  }
  return sortResult
}
